import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Menu } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { useUser } from "../contexts/UserContext";
import { doc, setDoc } from "firebase/firestore";
import { createPortalLinkFunction, db } from "../data/firebase";
import { useUpsellDialog } from "../contexts/UpsellDialogContext";
import { needsPaymentUpdate } from "../data/access";

const badge = (label: string, tone: "green" | "red") => (
  <span
    className={
      tone === "green"
        ? "inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20"
        : "inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-600 ring-1 ring-inset ring-red-600/20"
    }
  >
    {label}
  </span>
);

export function Account() {
  const { setOpenMobile } = useSidebar();
  const { currentUser, changePassword, access } = useAuth();
  const { userData } = useUser();
  const { openUpsell } = useUpsellDialog();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState(userData?.name || "");
  const [nameError, setNameError] = useState("");
  const [nameMessage, setNameMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const accessBadge = (() => {
    if (access.hasAccess) {
      if (access.accessSource === "pass" && access.accessExpiresAt) {
        return badge(
          `Access Active until ${new Date(access.accessExpiresAt).toLocaleDateString()}`,
          "green"
        );
      }
      return badge(access.stripeStatus === "trialing" ? "Trialing" : "Active", "green");
    }
    if (access.stripeStatus === "past_due") return badge("Past Due", "red");
    if (access.stripeStatus === "unpaid") return badge("Unpaid", "red");
    if (access.everEntitled) return badge("Access Expired", "red");
    return badge("Inactive", "red");
  })();

  useEffect(() => {
    if (userData?.name) {
      setName(userData.name);
    }
  }, [userData?.name]);

  async function handleNameChange(e: React.FormEvent) {
    e.preventDefault();

    if (!name.trim()) {
      return setNameError("Name cannot be empty");
    }

    try {
      setNameMessage("");
      setNameError("");
      setLoading(true);

      await setDoc(
        doc(db, "users", currentUser!.uid),
        { name: name.trim() },
        { merge: true }
      );

      setNameMessage("Name updated successfully");
    } catch (err) {
      setNameError("Failed to update name");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordChange(e: React.FormEvent) {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      return setPasswordError("Passwords do not match");
    }

    try {
      setPasswordMessage("");
      setPasswordError("");
      setLoading(true);
      await changePassword(newPassword);
      setPasswordMessage("Password updated successfully");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      setPasswordError("Failed to update password");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleBillingPortal = async () => {
    try {
      setPortalLoading(true);
      const { data } = await createPortalLinkFunction({
        returnUrl: window.location.origin,
      });
      // console.log("data", data);
      const portalUrl = (data as { url: string }).url;
      window.location.assign(portalUrl);
    } catch (error) {
      console.error("Failed to open billing portal:", error);
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="mt-12 sm:mt-0 flex-1 h-full overflow-y-auto">
      <div className="container max-w-2xl mx-auto pt-0 sm:pt-16 space-y-8 px-4 pb-60">
        {/* <div>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpenMobile(true)}
          >
            <Menu className="h-6 w-6" />
          </Button>
        </div> */}
        <div className="flex items-center gap-4 mt-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              My Account
            </h1>
            <p className="text-sm text-gray-500">
              Manage your account settings
            </p>
          </div>
        </div>

        <div className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center gap-4 mt-4">
              <h2 className="text-xl font-semibold">Subscription</h2>
              <div className="flex items-center gap-2">{accessBadge}</div>
            </div>

            {access.hasAccess && access.accessSource === "stripe" ? (
              <Button
                onClick={handleBillingPortal}
                disabled={portalLoading}
                variant="outline"
                className="w-fit"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait...
                  </>
                ) : (
                  "Manage Subscription"
                )}
              </Button>
            ) : access.hasAccess &&
              (access.accessSource === "sticky" ||
                access.accessSource === "admin") ? (
              <p className="text-sm text-gray-500">
                To cancel your subscription please email{" "}
                <a
                  href="mailto:support@optionstexting.com"
                  className="text-purple-600 hover:underline"
                >
                  support@optionstexting.com
                </a>
              </p>
            ) : needsPaymentUpdate(access) ? (
              <Button
                onClick={handleBillingPortal}
                disabled={portalLoading}
                variant="outline"
                className="w-fit"
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Please wait...
                  </>
                ) : (
                  "Update Payment Method"
                )}
              </Button>
            ) : !access.hasAccess ? (
              <Button
                variant="custom"
                onClick={openUpsell}
                className="w-fit shadow bg-gradient-to-br bg-purple-500 text-white font-bold hover:bg-purple-600"
              >
                Get Premium
              </Button>
            ) : null}
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Profile Information</h2>

            <div className="space-y-2">
              <h2 className=" font-medium">Email Address</h2>
              <p className="text-sm text-gray-500">{currentUser?.email}</p>
            </div>

            {nameError && (
              <div className="rounded-md bg-red-50 p-4 text-sm text-red-500">
                {nameError}
              </div>
            )}

            {nameMessage && (
              <div className="rounded-md bg-green-50 p-4 text-sm text-green-500">
                {nameMessage}
              </div>
            )}

            <form onSubmit={handleNameChange} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white"
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !name.trim() || name === userData?.name}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update Name"
                )}
              </Button>
            </form>

            <div className="space-y-2">
              <h2 className="text-xl font-semibold mt-8">Change Password</h2>

              {passwordError && (
                <div className="rounded-md bg-red-50 p-4 text-sm text-red-500">
                  {passwordError}
                </div>
              )}

              {passwordMessage && (
                <div className="rounded-md bg-green-50 p-4 text-sm text-green-500">
                  {passwordMessage}
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="new-password" className="text-sm font-medium">
                    New Password
                  </label>
                  <Input
                    id="new-password"
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="confirm-password"
                    className="text-sm font-medium"
                  >
                    Confirm New Password
                  </label>
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="bg-white"
                  />
                </div>

                <Button type="submit" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Password"
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
